/**
 * The file list of a zip the user selected, read in the browser from its central directory, so the
 * user chooses the installer before uploading. It only offers a choice: the service checks the zip
 * itself when the job is submitted.
 */

export interface ZipEntry {
  name: string
  size: number
}

export interface ZipListing {
  entries: ZipEntry[]
  encrypted: boolean
  /** A non-ASCII file name not stored as UTF-8, which the service refuses */
  legacyName: string | null
}

const EOCD_SIGNATURE = 0x06054b50
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50
const ZIP64_EOCD_SIGNATURE = 0x06064b50
const CENTRAL_SIGNATURE = 0x02014b50
const EOCD_MIN_BYTES = 22
const MAX_COMMENT_BYTES = 65535
const CENTRAL_ENTRY_BYTES = 46
const MAX_U16 = 0xffff
const MAX_U32 = 0xffffffff
const FLAG_ENCRYPTED = 0x1
const FLAG_UTF8 = 0x800
const ZIP64_EXTRA_ID = 0x0001
/** A directory larger than this is not installer media; the service's entry limit refuses it too */
const MAX_DIRECTORY_BYTES = 64 * 1024 * 1024

const METADATA_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini'])

/** jsdom's Blob has no arrayBuffer(), and FileReader works in every browser */
function readBytes(file: Blob, start: number, end: number): Promise<DataView> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new DataView(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read'))
    reader.readAsArrayBuffer(file.slice(start, end))
  })
}

function u64(view: DataView, at: number): number {
  return view.getUint32(at, true) + view.getUint32(at + 4, true) * 2 ** 32
}

function unreadable(): Error {
  return new Error('This file is not a readable zip.')
}

/** Offset and size of the central directory */
async function locateDirectory(file: Blob): Promise<{ offset: number; size: number }> {
  if (file.size < EOCD_MIN_BYTES) throw unreadable()
  const tailStart = Math.max(0, file.size - EOCD_MIN_BYTES - MAX_COMMENT_BYTES)
  const tail = await readBytes(file, tailStart, file.size)
  let at = tail.byteLength - EOCD_MIN_BYTES
  while (at >= 0 && tail.getUint32(at, true) !== EOCD_SIGNATURE) at--
  if (at < 0) throw unreadable()

  const count = tail.getUint16(at + 10, true)
  const size = tail.getUint32(at + 12, true)
  const offset = tail.getUint32(at + 16, true)
  if (count !== MAX_U16 && size !== MAX_U32 && offset !== MAX_U32) return { offset, size }

  const locatorAt = at - 20
  if (locatorAt < 0 || tail.getUint32(locatorAt, true) !== ZIP64_LOCATOR_SIGNATURE) throw unreadable()
  const recordAt = u64(tail, locatorAt + 8)
  const record = await readBytes(file, recordAt, recordAt + 56)
  if (record.byteLength < 56 || record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE) throw unreadable()
  return { offset: u64(record, 48), size: u64(record, 40) }
}

export async function readZipListing(file: Blob): Promise<ZipListing> {
  const { offset, size } = await locateDirectory(file)
  if (size > MAX_DIRECTORY_BYTES || offset + size > file.size) throw unreadable()
  const directory = await readBytes(file, offset, offset + size)
  const bytes = new Uint8Array(directory.buffer, directory.byteOffset, directory.byteLength)
  const utf8 = new TextDecoder('utf-8')

  const listing: ZipListing = { entries: [], encrypted: false, legacyName: null }
  let at = 0
  while (at + CENTRAL_ENTRY_BYTES <= directory.byteLength) {
    if (directory.getUint32(at, true) !== CENTRAL_SIGNATURE) throw unreadable()
    const flags = directory.getUint16(at + 8, true)
    let entrySize = directory.getUint32(at + 24, true)
    const nameLength = directory.getUint16(at + 28, true)
    const extraLength = directory.getUint16(at + 30, true)
    const commentLength = directory.getUint16(at + 32, true)
    const nameBytes = bytes.subarray(at + CENTRAL_ENTRY_BYTES, at + CENTRAL_ENTRY_BYTES + nameLength)
    const name = utf8.decode(nameBytes)

    if (entrySize === MAX_U32) {
      let extra = at + CENTRAL_ENTRY_BYTES + nameLength
      const extraEnd = extra + extraLength
      while (extra + 4 <= extraEnd) {
        const id = directory.getUint16(extra, true)
        const length = directory.getUint16(extra + 2, true)
        if (id === ZIP64_EXTRA_ID && length >= 8) entrySize = u64(directory, extra + 4)
        extra += 4 + length
      }
    }

    if (flags & FLAG_ENCRYPTED) listing.encrypted = true
    if (!listing.legacyName && !(flags & FLAG_UTF8) && nameBytes.some((b) => b > 0x7f)) listing.legacyName = name
    listing.entries.push({ name, size: entrySize })
    at += CENTRAL_ENTRY_BYTES + nameLength + extraLength + commentLength
  }
  return listing
}

/** A file an operating system adds to folders: never part of what an installer needs */
export function isMetadataFile(name: string): boolean {
  const parts = name.replace(/\\/g, '/').split('/')
  const base = parts[parts.length - 1]
  return parts.includes('__MACOSX') || base.startsWith('._') || METADATA_FILES.has(base.toLowerCase())
}

/** The entries the user can choose as the installer: .msi first, then .exe, in zip order */
export function installerCandidates(entries: ZipEntry[]): ZipEntry[] {
  const kept = entries.filter((e) => !e.name.endsWith('/') && !isMetadataFile(e.name))
  const withSuffix = (suffix: string) => kept.filter((e) => e.name.toLowerCase().endsWith(suffix))
  return [...withSuffix('.msi'), ...withSuffix('.exe')]
}

/** The candidate to preselect: only when the zip holds exactly one MSI */
export function preselectedInstaller(candidates: ZipEntry[]): string {
  const msis = candidates.filter((c) => c.name.toLowerCase().endsWith('.msi'))
  return msis.length === 1 ? msis[0].name : ''
}
